import { useEffect, useContext, useMemo } from "react";
import { useTranslation } from "react-i18next";
import AdPlanForm from "./AdPlanForm";
import useAdPlanState from "./useAdPlanState";
import { useCreateAdPlan } from "./useCreateAdPlan";
import { useUpdateAdPlan } from "./useUpdateAdPlan";
import AdPlanError from "./AdPlanError";
import { AdPlanContext } from "./AdPlanContext";
import { useAppSelector } from "../../../../redux/hooks";
import { Modal } from "../../basic/Modal";
import { useSimpleCan } from "../../../../config/casl/casl";

const AdPlanModal = (props) => {
  const { t } = useTranslation(["common"]);
  const initialAdPlan = props.initialAdPlan || {};

  const onboardStatus = useAppSelector((state) => state.amz_onboard.org_status);
  const marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  const user = useAppSelector((state) => state.user);
  const channel = useAppSelector((state) => state.channel);
  const userCanCreateAds = useSimpleCan("create", "Ad");

  const {
    adPlan,
    errors,
    isValid,
    onChange,
    clear,
    updateChild,
    removeChild,
    brandOptions,
    savedVideos,
    storeOptions,
    logoOptions,
    pageOptions,
    agreeToArchive,
    setAgreeToArchive,
    apiStatus,
  } = useAdPlanState(
    initialAdPlan,
    marketPlace,
    props.categoryId,
    props.adId ?? null,
    user?.preferences?.acos_or_roas === "roas",
    user.organization_type,
  );
  const createAdPlan = useCreateAdPlan();
  const updateAdPlan = useUpdateAdPlan({ adId: props.adId });
  const { resetAdPlanContext } = useContext(AdPlanContext);

  const isSubscriptionBlocked = ["pre_trial", "qore_active", "trial_expired"].includes(onboardStatus);
  const isOpen = props.isOpen && !isSubscriptionBlocked;

  const formattedCategories = useMemo(
    () =>
      props.categories?.map((cat) => ({
        value: cat.id ?? cat.value,
        label: cat.leaf_category_name ?? cat.label,
      })) ?? [],
    [props.categories],
  );

  const handleCreate = async (adPlan, notify = true) => {
    try {
      await createAdPlan.mutateAsync({ adPlan, adId: props.adId, notify });
      return true;
    } catch {
      return undefined;
    }
  };

  const handleUpdate = (adPlan) => {
    updateAdPlan.mutate({ adPlan });
  };

  const handleClose = () => {
    clear();
    resetAdPlanContext();
    props.onClose?.();
  };

  useEffect(() => {
    clear();

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [props.categoryId]);

  if (!userCanCreateAds) {
    return null;
  }

  return (
    <>
      <Modal
        isOpen={isOpen}
        isDismissable={false}
        onOpenChange={(open) => {
          if (!open) {
            handleClose();
          }
        }}
        css={{ width: "1200px", margin: 0, paddingTop: "50px" }}
      >
        {props.loading ?
          t(($) => $.common["Loading..."])
        : <AdPlanForm
            onChange={onChange}
            state={adPlan}
            errors={errors}
            isValid={isValid}
            onRemoveChild={(i) => removeChild(i)}
            categories={formattedCategories}
            savedVideos={savedVideos}
            brandOptions={brandOptions}
            storeOptions={storeOptions}
            logoOptions={logoOptions}
            pageOptions={pageOptions}
            dashboard={props.dashboard || props.assetShortcutModal}
            user={user}
            marketPlace={marketPlace}
            channel={channel.currentChannel}
            agreeToArchive={agreeToArchive}
            setAgreeToArchive={setAgreeToArchive}
            apiStatus={apiStatus}
            parentDailyBudget={props.parentDailyBudget}
            isModal={true}
            closeModal={() => {
              clear();
              props.onClose?.();
            }}
            sectionEdit={props.sectionEdit}
            onUpdate={handleUpdate}
            onCreate={handleCreate}
            clearForm={clear}
            addingNewChild={adPlan.id && adPlan.canHaveChildren}
            onParentUpdate={props.onParentUpdate}
            updateChild={updateChild}
            panelEditStep={props.editStep}
            isProcessing={createAdPlan.isPending || updateAdPlan.isPending}
          />
        }
      </Modal>
      <AdPlanError
        errors={props.isOpen && isSubscriptionBlocked ? { adPlanError: "No subscription" } : null}
        clearError={() => props.onClose?.()}
      />
    </>
  );
};

export default AdPlanModal;
